import { getDisplayNodeLabel } from '@/helpers/graph.helpers';
import type { GraphNode, GraphRelationship } from '@/types/graph.types';

export interface AnalysisRelationshipHop {
  relationship: string;
  targetLabel: string;
  direction: 'out' | 'in';
}

export interface AnalysisRelationshipPath {
  hops: AnalysisRelationshipHop[];
}

export interface AnalysisReachablePathOption {
  id: string;
  path: AnalysisRelationshipPath;
  terminalLabel: string;
  label: string;
  /**
   * Why this path cannot be taken, or absent when it can.
   *
   * A refused path is still LISTED. Dropping it was the defect: a path that
   * simply is not there looks identical to one the schema does not contain, so
   * an author retraced the same dead end without ever learning why it was dead.
   */
  refusedReason?: string;
}

/**
 * A structural ceiling, not the rule.
 *
 * The rule is about fan-out — see `analysisPathRefusal` — and to-one hops are
 * unlimited under it. But the schema meta-graph is cyclic (27 labels and 49
 * relationships in a mid-size project), so an unbounded walk enumerates paths
 * faster than anyone can read them. This is the depth past which the picker
 * stops looking and says so, rather than a claim that the path is invalid.
 */
const MAX_PATH_HOPS = 6;

const nodeLabelById = (
  nodes: GraphNode[],
): Map<string, string> => {
  const map = new Map<string, string>();
  nodes.forEach((node) => {
    map.set(String(node.id), getDisplayNodeLabel(node.labels));
  });
  return map;
};

const hopLabel = (hop: AnalysisRelationshipHop): string =>
  hop.direction === 'out'
    ? `→ ${hop.relationship} → ${hop.targetLabel}`
    : `← ${hop.relationship} ← ${hop.targetLabel}`;

export const formatAnalysisPathLabel = (
  sourceLabel: string,
  path: AnalysisRelationshipPath,
): string =>
  [sourceLabel, ...path.hops.map(hopLabel)].join(' ');

export const analysisRelationshipPathId = (
  path: AnalysisRelationshipPath,
): string =>
  path.hops
    .map((hop) => `${hop.direction}:${hop.relationship}:${hop.targetLabel}`)
    .join('|');

export function buildSchemaAdjacency(
  nodes: GraphNode[],
  relationships: GraphRelationship[],
): Map<string, AnalysisRelationshipHop[]> {
  const labelsById = nodeLabelById(nodes);
  const adjacency = new Map<string, AnalysisRelationshipHop[]>();

  const pushHop = (fromLabel: string, hop: AnalysisRelationshipHop) => {
    const existing = adjacency.get(fromLabel) ?? [];
    if (
      existing.some(
        (candidate) =>
          candidate.relationship === hop.relationship &&
          candidate.targetLabel === hop.targetLabel &&
          candidate.direction === hop.direction,
      )
    ) {
      return;
    }
    adjacency.set(fromLabel, [...existing, hop]);
  };

  relationships.forEach((rel) => {
    const relType = rel.type ?? rel.caption ?? '';
    if (!relType) return;
    const fromLabel = labelsById.get(String(rel.from));
    const toLabel = labelsById.get(String(rel.to));
    if (!fromLabel || !toLabel || fromLabel === toLabel) return;
    pushHop(fromLabel, {
      relationship: relType,
      targetLabel: toLabel,
      direction: 'out',
    });
    pushHop(toLabel, {
      relationship: relType,
      targetLabel: fromLabel,
      direction: 'in',
    });
  });

  return adjacency;
}

/**
 * Lists relationship paths reachable from the current table label within the
 * schema meta-graph.
 *
 * Paths the walk will not extend are still returned, carrying `refusedReason`,
 * so the picker can show them greyed with an explanation instead of leaving a
 * gap the author has to guess at.
 */
export function listReachableAnalysisPaths(
  sourceLabel: string,
  nodes: GraphNode[],
  relationships: GraphRelationship[],
): AnalysisReachablePathOption[] {
  if (!sourceLabel.trim()) return [];
  const adjacency = buildSchemaAdjacency(nodes, relationships);
  const results: AnalysisReachablePathOption[] = [];
  const seenPathIds = new Set<string>();

  const pushRefused = (
    hops: AnalysisRelationshipHop[],
    terminalLabel: string,
    refusedReason: string,
  ) => {
    const path: AnalysisRelationshipPath = { hops: [...hops] };
    const id = analysisRelationshipPathId(path);
    if (seenPathIds.has(id)) return;
    seenPathIds.add(id);
    results.push({
      id,
      path,
      terminalLabel,
      label: formatAnalysisPathLabel(sourceLabel, path),
      refusedReason,
    });
  };

  const visit = (
    currentLabel: string,
    hops: AnalysisRelationshipHop[],
    visitedLabels: Set<string>,
  ) => {
    if (hops.length > 0) {
      const path: AnalysisRelationshipPath = { hops: [...hops] };
      const id = analysisRelationshipPathId(path);
      if (!seenPathIds.has(id)) {
        seenPathIds.add(id);
        results.push({
          id,
          path,
          terminalLabel: currentLabel,
          label: formatAnalysisPathLabel(sourceLabel, path),
        });
      }
    }
    const nextHops = adjacency.get(currentLabel) ?? [];
    nextHops.forEach((hop) => {
      // Both of these used to `return` — the path vanished and the author was
      // told nothing. They are listed as refused instead, with the reason.
      if (visitedLabels.has(hop.targetLabel)) {
        pushRefused(
          [...hops, hop],
          hop.targetLabel,
          `This comes back to ${hop.targetLabel}, which the path already went through — a loop would multiply the same records against themselves`,
        );
        return;
      }
      if (hops.length >= MAX_PATH_HOPS) {
        pushRefused(
          [...hops, hop],
          hop.targetLabel,
          `Further than ${MAX_PATH_HOPS} relationships from ${sourceLabel}; the picker stops looking here rather than listing every route in the schema`,
        );
        return;
      }
      const nextVisited = new Set(visitedLabels);
      nextVisited.add(hop.targetLabel);
      visit(hop.targetLabel, [...hops, hop], nextVisited);
    });
  };

  visit(sourceLabel, [], new Set([sourceLabel]));
  return results.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The drill depth at which a selected path is listed — its hops minus the last.
 *
 * The picker lists one hop level at a time, so a multi-hop selection is only the
 * "current" option once the picker has drilled past its earlier hops. Opening at
 * depth 0 instead shows the first-hop list with *nothing* selected, because at
 * that depth every candidate id is a one-hop id while the selection is a
 * multi-hop one.
 */
export function drilledHopsForSelectedPath(
  options: AnalysisReachablePathOption[],
  selectedId: string | null,
): AnalysisRelationshipHop[] {
  if (!selectedId) return [];
  const selected = options.find((option) => option.id === selectedId);
  if (!selected || selected.path.hops.length <= 1) return [];
  return selected.path.hops.slice(0, -1);
}
